import type { ColumnDef } from "@tanstack/react-table";
import { EmailReactTable } from "components/EmailReactTable";
import { RelativeTimeDisplay } from "components/RelativeTimeDisplay/RelativeTimeDisplay";
import { Badge } from "components/ui/Badge";
import { Button } from "components/ui/Button";
import { HYPHEN } from "constants/search-table";
import { type FC, useCallback } from "react";
import type {
  EmailWithStatus,
  SelectContextMenuHandler,
} from "routes/emails/SearchEmailsPage";
import { FreeWordHighlighter } from "routes/emails/SearchEmailsPage/components/FreeWordHighlighter";
import { searchGmail } from "utils/gmail-utils";
import { formatToManYen, omitText } from "utils/text-utils";
import { ReactComponent as GmailIcon } from "../../../../../components/EmailCard/gmail.svg";
import { ReactComponent as StarIcon } from "../star.svg";

export type MatterEmailsTableUIProps = {
  emails: EmailWithStatus[];
  onSelectEmail: (id: string) => void;
  onToggleFavoriteEmail: (emailId: string, favorite: boolean) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  freeWords: string[];
  selectedId?: string | null;
};

export type MatterRow = {
  emailId: string;
  read: boolean;
  star: JSX.Element;
  from: JSX.Element;
  dateTime: JSX.Element;
  duplicateCount: JSX.Element;
  station: JSX.Element;
  isRemoteEnabled: JSX.Element;
  period: JSX.Element;
  reward: string;
  workingTimeWidth: JSX.Element;
  otherCountriesEnabled: JSX.Element;
  age: string;
  numberOfInterviews: string;
  commercialDistributionLimitation: JSX.Element;
  endOrPrimeContractorDirect: JSX.Element;
  menu: JSX.Element;
};

export const MatterEmailsTableUI: FC<MatterEmailsTableUIProps> = ({
  emails,
  onSelectEmail,
  onSelectContextMenu,
  onToggleFavoriteEmail,
  freeWords,
  selectedId = null,
}) => {
  const columns: ColumnDef<MatterRow, any>[] = [
    {
      header: "emailId",
      accessorKey: "emailId",
    },
    {
      accessorKey: "star",
      header: "☆",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "from",
      header: "From",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "dateTime",
      header: "日付",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "duplicateCount",
      header: "重複",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "station",
      header: "現場駅",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "isRemoteEnabled",
      header: "リモート",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "period",
      header: "期間",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "reward",
      header: "金額",
    },
    {
      accessorKey: "workingTimeWidth",
      header: "精算幅、固定",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "otherCountriesEnabled",
      header: "外国籍ＯＫ",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "age",
      header: "年齢制限",
    },
    {
      accessorKey: "numberOfInterviews",
      header: "面談回数",
    },
    {
      accessorKey: "commercialDistributionLimitation",
      header: "商流制限",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "endOrPrimeContractorDirect",
      header: "元請直/エンド直",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "menu",
      header: "",
      cell: props => props.getValue(),
    },
  ];

  const endOrPrimeContractorDirectText = useCallback(
    (endOrPrimeContractorDirect: string | null) => {
      if (!endOrPrimeContractorDirect) {
        return HYPHEN;
      }
      if (endOrPrimeContractorDirect === "direct") {
        return "エンド直";
      } else if (endOrPrimeContractorDirect === "prime") {
        return "元請直";
      } else if (endOrPrimeContractorDirect === "others") {
        return "元請け以下";
      }
      return HYPHEN;
    },
    [],
  );

  const numberOfInterviewsText = useCallback(
    (numberOfInterviews: string | null) => {
      if (!numberOfInterviews) {
        return HYPHEN;
      }
      if (Number.isNaN(Number(numberOfInterviews))) {
        return HYPHEN;
      }
      return `${numberOfInterviews}回`;
    },
    [],
  );

  const data: MatterRow[] = emails.map((email: EmailWithStatus) => ({
    emailId: email.id,
    read: email.read,
    // お気に入りボタン
    star: (
      <StarIcon
        className={`${
          email.favorite ? "text-yellow-300" : "text-gray-200"
        } w-5 h-5`}
        onClick={e => {
          onToggleFavoriteEmail(email.id, !email.favorite);
          e.stopPropagation();
        }}
      />
    ),
    // 差し出し元 (名前のみ表示、メールアドレスはホバー時 tooltip)
    from: (
      <div
        className="tooltip tooltip-right relative"
        data-tip={email.from.address}
      >
        {!email.read && (
          <div className="absolute -left-4 top-1">
            <div className="badge badge-xs badge-info opacity-80 scale-75 animate-[pulse_3s_ease-in-out_infinite]" />
          </div>
        )}
        <span className="text-xs">{omitText(email.from.name, 16)}</span>
      </div>
    ),
    // 日付
    dateTime: <RelativeTimeDisplay date={email.date} />,
    // 重複
    duplicateCount:
      email.duplicateCount != null && email.duplicateCount >= 2 ? (
        <Badge color="secondary" isOutline size="xs">
          {email.duplicateCount}件
        </Badge>
      ) : (
        <>{HYPHEN}</>
      ),
    // 現場駅
    station: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(email?.matterColumn?.station)}
      />
    ),
    // リモート
    isRemoteEnabled: email?.matterColumn?.isRemoteEnabled ? (
      <Badge color="success" isOutline size="xs">
        OK
      </Badge>
    ) : (
      <Badge color="error" isOutline size="xs">
        NG
      </Badge>
    ),
    // 期間
    period: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(email?.matterColumn?.period)}
      />
    ),
    // 金額
    reward: formatToManYen(email?.matterColumn?.reward),
    // 精算幅、固定
    workingTimeWidth: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(email?.matterColumn?.workingTimeWidth)}
      />
    ),
    // 外国籍ＯＫ
    otherCountriesEnabled: email?.matterColumn?.otherCountriesEnabled ? (
      <Badge color="success" isOutline size="xs">
        OK
      </Badge>
    ) : (
      <Badge color="error" isOutline size="xs">
        NG
      </Badge>
    ),
    // 年齢
    age: email?.hrColumn?.age ? `${email?.hrColumn?.age}歳` : HYPHEN,
    // 面談回数
    numberOfInterviews: numberOfInterviewsText(
      email?.matterColumn?.numberOfInterviews || null,
    ),
    // 商流制限
    commercialDistributionLimitation: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(
          email?.matterColumn?.commercialDistributionLimitation,
        )}
      />
    ),
    // 元請直/エンド直
    endOrPrimeContractorDirect: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(
          endOrPrimeContractorDirectText(
            email?.matterColumn?.endOrPrimeContractorDirect || null,
          ),
        )}
      />
    ),
    // メニュー
    menu: (
      <div className="dropdown dropdown-end">
        <Button
          tabIndex={0}
          variant="ghost"
          size="xs"
          onClick={e => e.stopPropagation()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            className="h-4 w-4 stroke-current"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
            />
          </svg>
        </Button>
        <ul
          className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52"
          onClick={e => e.stopPropagation()}
        >
          {/* Gmail検索ボタン */}
          <li>
            <a
              onClick={e => {
                e.stopPropagation();
                searchGmail(email.subject, email.inReplyToMessageId);
              }}
            >
              <GmailIcon className="w-4 h-4 inline-block mr-2" />
              Gmailで表示
            </a>
          </li>
        </ul>
      </div>
    ),
  }));
  return data.length === 0 ? (
    <div className="flex justify-center">
      <div className="text-left text-gray-500 max-w-xl">
        <p>条件に合う検索結果はありません。</p>
        <div className="h-4" />
        <p>あるいは、あなたのアカウントがどの会社にも紐づいていません。</p>
        <p>管理者にアカウントと所属会社の紐づけ作業を依頼してください。</p>
      </div>
    </div>
  ) : (
    <EmailReactTable
      data={data}
      columns={columns}
      onSelectEmail={onSelectEmail}
      onSelectContextMenu={onSelectContextMenu}
      selectedId={selectedId}
    />
  );
};
